import React, { useEffect, useState } from "react";
import { projectsAPI, tenantAPI } from "../../api/client";
import CustomerSelect from "./CustomerSelect";
import ProjectDocumentsField from "./ProjectDocumentsField";
import "./Projects.css";
import ToolTip from "../layout/ToolTip";

export default function AddProjectModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    customerId: "",
    workflowId: "",
    durationValue: "",
    durationUnit: "DAYS",
    imageFiles: [],
    tempViewUrls: [],
    stlFiles: [],
    documentFiles: [],
    initialAllocatorUserIds: [],
    folderId: "",
  });
  const [workflows, setWorkflows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allocators, setAllocators] = useState([]);
  const [customFieldsConfig, setCustomFieldsConfig] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [systemFieldConfig, setSystemFieldConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const sysField = (key) => ({
    visible: systemFieldConfig[key]?.visible !== false,
    required: systemFieldConfig[key]?.required === true,
  });

  const handleModalClose = () => {
    (form.tempViewUrls || []).forEach((url) => {
      if (url.startsWith("blob:")) URL.revokeObjectURL(url);
    });
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [workflowsResp, usersResp, tenantResp, foldersResp] = await Promise.all([
          projectsAPI.workflowOptions(),
          projectsAPI.allocationEligibleUsers(),
          tenantAPI.getTenant(),
          projectsAPI.getFolders(),
        ]);
        setWorkflows(
          Array.isArray(workflowsResp.data) ? workflowsResp.data : [],
        );
        setAllocators(Array.isArray(usersResp.data) ? usersResp.data : []);
        setFolders(Array.isArray(foldersResp.data) ? foldersResp.data : []);
        const tenant = tenantResp?.data || {};
        const schema = tenant.projectFormSchema || {};
        const fields = Array.isArray(schema.fields) ? schema.fields : [];
        setSystemFieldConfig(schema.systemFieldConfig || {});
        setCustomFieldsConfig(fields);
        setCustomFieldValues(
          fields.reduce((acc, field) => {
            acc[field.key] = field.type === "select" ? "" : "";
            return acc;
          }, {}),
        );
      } catch (err) {
        setError("Failed to load create-project options");
      }
    };
    load();
  }, [open]);

  const handleImage = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    const nextPreviewUrls = nextFiles.map((file) => URL.createObjectURL(file));
    setForm((prev) => {
      (prev.tempViewUrls || []).forEach((url) => URL.revokeObjectURL(url));
      return {
        ...prev,
        imageFiles: nextFiles,
        tempViewUrls: nextPreviewUrls,
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (!form.customerId) {
        setError("Please select a customer.");
        setSaving(false);
        return;
      }
      if (sysField("description").required && !String(form.description || "").trim()) {
        setError("Description is required.");
        setSaving(false);
        return;
      }
      if (sysField("images").required && (form.imageFiles || []).length === 0) {
        setError("At least one project image is required.");
        setSaving(false);
        return;
      }
      if (sysField("stlFiles").required && (form.stlFiles || []).length === 0) {
        setError("At least one 3D model file is required.");
        setSaving(false);
        return;
      }
      if (sysField("documents").required && (form.documentFiles || []).length === 0) {
        setError("At least one project document is required.");
        setSaving(false);
        return;
      }
      if (sysField("folder").required && !form.folderId) {
        setError("Please assign the project to a folder.");
        setSaving(false);
        return;
      }
      if (sysField("initialAllocation").required && (form.initialAllocatorUserIds || []).length === 0) {
        setError("Please allocate at least one user.");
        setSaving(false);
        return;
      }
      const requiredMissing = customFieldsConfig.filter((field) => {
        if (!field.required) return false;
        const value = customFieldValues[field.key];
        return !String(value || "").trim();
      });
      if (requiredMissing.length) {
        setError("Please fill all required custom fields.");
        setSaving(false);
        return;
      }

      const payload = new FormData();
      payload.append("name", form.name);
      payload.append("description", form.description || "");
      payload.append("customerId", form.customerId || "");
      payload.append("workflowId", form.workflowId);
      payload.append("durationValue", String(Number(form.durationValue)));
      payload.append("durationUnit", form.durationUnit);
      payload.append("customFields", JSON.stringify(customFieldValues || {}));
      (form.imageFiles || []).forEach((file) => payload.append("images", file));
      (form.stlFiles || []).forEach((file) => payload.append("images", file));
      (form.documentFiles || []).forEach((file) =>
        payload.append("documents", file),
      );

      const resp = await projectsAPI.create(payload);
      await Promise.all([
        ...(Array.isArray(form.initialAllocatorUserIds)
          ? form.initialAllocatorUserIds.map((userId) =>
              projectsAPI.allocateUser(resp.data.id, userId),
            )
          : []),
        ...(form.folderId
          ? [projectsAPI.moveProjectsToFolder([resp.data.id], form.folderId)]
          : []),
      ]);
      onCreated(resp.data);
      handleModalClose();
      setForm({
        name: "",
        description: "",
        customerId: "",
        workflowId: "",
        durationValue: "",
        durationUnit: "DAYS",
        imageFiles: [],
        tempViewUrls: [],
        stlFiles: [],
        documentFiles: [],
        initialAllocatorUserIds: [],
        folderId: "",
      });
      setCustomFieldValues({});
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop">
      <form className="modal-content improved-modal" onSubmit={submit}>
        <div className="modal-header">
          <h2>Add New Project</h2>
          <button type="button" className="close-btn" onClick={handleModalClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* BASIC INFO */}
          <div className="form-section">
            <div>
              <label>Project Name <span style={{ color: "#ef4444" }}>*</span></label>{" "}
              <ToolTip
                toolTipTitle="Project name"
                toolTipText="The name of the project, e.g. New Onboarding."
              />
            </div>

            <input
              required
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />
            {sysField("description").visible && (
              <>
                <label>
                  Description
                  {sysField("description").required && <span style={{ color: "#ef4444" }}> *</span>}
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </>
            )}
          </div>

          {/* CUSTOMER & WORKFLOW */}
          <div className="form-section two-col">
            <div>
              <label>Customer <span style={{ color: "#ef4444" }}>*</span></label>
              <CustomerSelect
                value={form.customerId}
                onChange={(customerId) =>
                  setForm((prev) => ({ ...prev, customerId }))
                }
              />
            </div>

            <div>
              <label>Workflow <span style={{ color: "#ef4444" }}>*</span></label>
              <select
                required
                value={form.workflowId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, workflowId: e.target.value }))
                }
              >
                <option value="">Select workflow...</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* DURATION */}
          <div className="form-section two-col">
            <div>
              <label>Duration <span style={{ color: "#ef4444" }}>*</span></label>
              <input
                required
                type="number"
                min="1"
                value={form.durationValue}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    durationValue: e.target.value,
                  }))
                }
              />
            </div>

            <div>
              <label>Unit</label>
              <select
                value={form.durationUnit}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    durationUnit: e.target.value,
                  }))
                }
              >
                <option value="HOURS">Hours</option>
                <option value="DAYS">Days</option>
              </select>
            </div>
          </div>

          {/* IMAGE */}
          {sysField("images").visible && (
            <>
              <div className="form-section">
                <label>
                  Project Images
                  {sysField("images").required && <span style={{ color: "#ef4444" }}> *</span>}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImage(e.target.files)}
                />
              </div>

              {form.tempViewUrls?.length > 0 && (
                <div className="project-image-preview-list">
                  {form.tempViewUrls.map((url, index) => (
                    <img
                      key={url}
                      className="project-image-preview"
                      src={url}
                      alt={`project-image-${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* STL FILES */}
          {sysField("stlFiles").visible && (
            <div className="form-section">
              <label>
                3D Model Files (.stl)
                {sysField("stlFiles").required && <span style={{ color: "#ef4444" }}> *</span>}
              </label>
              <input
                type="file"
                accept=".stl"
                multiple
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    stlFiles: Array.from(e.target.files || []),
                  }))
                }
              />
              {form.stlFiles?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {form.stlFiles.map((file, i) => (
                    <div key={i} className="small-muted">
                      {file.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PROJECT DOCUMENTS */}
          {sysField("documents").visible && (
            <ProjectDocumentsField
              files={form.documentFiles}
              required={sysField("documents").required}
              onFilesChange={(documentFiles) =>
                setForm((prev) => ({ ...prev, documentFiles }))
              }
            />
          )}

          {/* FOLDER */}
          {folders.length > 0 && sysField("folder").visible && (
            <div className="form-section">
              <label>
                Assign to Folder
                {sysField("folder").required
                  ? <span style={{ color: "#ef4444" }}> *</span>
                  : <span className="small-muted"> (Optional)</span>}
              </label>
              <select
                value={form.folderId}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, folderId: e.target.value }))
                }
              >
                <option value="">No folder</option>
                {folders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ALLOCATION */}
          {sysField("initialAllocation").visible && (
          <div className="form-section">
            <label>
              Initial Allocation
              {sysField("initialAllocation").required
                ? <span style={{ color: "#ef4444" }}> *</span>
                : <span className="small-muted"> (Optional)</span>}
            </label>
            <select
              multiple
              size={Math.min(Math.max(allocators.length, 3), 6)}
              value={form.initialAllocatorUserIds}
              onChange={(e) => {
                const selectedIds = Array.from(
                  e.target.selectedOptions || [],
                ).map((option) => option.value);
                setForm((prev) => ({
                  ...prev,
                  initialAllocatorUserIds: selectedIds,
                }));
              }}
            >
              {allocators.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName}
                  {user.roles?.length
                    ? ` (${user.roles.map((role) => role.name).join(", ")})`
                    : user.role?.name
                      ? ` (${user.role.name})`
                      : ""}
                </option>
              ))}
            </select>
            <small className="small-muted">
              Hold Ctrl/Cmd to select multiple users.
            </small>
          </div>
          )}

          {customFieldsConfig.length > 0 && (
            <div className="form-section">
              <h4 style={{ marginBottom: 6 }}>Additional Fields</h4>
              <div className="modal-grid two-col">
                {customFieldsConfig.map((field) => (
                  <div key={field.key}>
                    <label>
                      {field.label}
                      {field.required && <span style={{ color: "#ef4444" }}> *</span>}
                    </label>
                    {field.type === "select" ? (
                      <select
                        value={customFieldValues[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        required={field.required}
                      >
                        <option value="">Select...</option>
                        {(field.options || []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={customFieldValues[field.key] || ""}
                        onChange={(e) =>
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [field.key]: e.target.value,
                          }))
                        }
                        required={field.required}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Creating..." : "Create Project"}
          </button>
        </div>
      </form>
    </div>
  );
}
